import React, { useState, useRef, useCallback, useEffect } from "react";
import { Camera } from "lucide-react";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { firebaseApp } from "../../../providers/firebase";
import {
  createHubPost,
  getInitials,
  resolveAuthorNameForPost,
} from "../../../data/communityHub";
import { useAuth } from "../../../providers/auth";
import { useUser } from "../../../providers/database";
import toast from "react-hot-toast";
import { useQueryClient } from "react-query";

const storage = getStorage(firebaseApp);

function FeedAvatar({
  initials,
  name,
  avatarUrl,
  size = 44,
}: {
  initials: string;
  name: string;
  avatarUrl?: string;
  size?: number;
}) {
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name}
        className="rounded-full object-cover shrink-0"
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className="rounded-full text-white font-bold grid place-items-center shrink-0 bg-[#15415E]"
      style={{ width: size, height: size, fontSize: size * 0.38 }}
    >
      {initials}
    </div>
  );
}

export default function CommunityPostComposer({
  hubId,
}: {
  hubId: string;
}) {
  const { user }: any = useAuth();
  const { data: userProfile }: any = useUser(user?.uid);
  const qc = useQueryClient();
  const [body, setBody] = useState("");
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const textareaRef = useRef(null) as any;
  const [authorName, setAuthorName] = useState("");

  useEffect(() => {
    let cancelled = false;
    resolveAuthorNameForPost(user, userProfile).then((name) => {
      if (!cancelled) setAuthorName(name);
    });
    return () => {
      cancelled = true;
    };
  }, [user, userProfile]);

  const autoResize = useCallback(() => {
    const el = textareaRef.current;
    if (el) {
      el.style.height = "auto";
      el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
    }
  }, []);

  const uploadImages = async () => {
    const urls: string[] = [];
    for (const file of files) {
      const path = `community/hubs/${hubId}/posts/${Date.now()}-${file.name}`;
      const fileRef = ref(storage, path);
      await uploadBytes(fileRef, file);
      urls.push(await getDownloadURL(fileRef));
    }
    return urls;
  };

  const handleSubmit = async () => {
    if (!body.trim() && files.length === 0) return;
    setUploading(true);
    try {
      const images = files.length ? await uploadImages() : [];
      const trimmedBody = body.trim();
      const authorName = await resolveAuthorNameForPost(user, userProfile);
      await createHubPost(hubId, {
        authorName,
        authorAvatarUrl:
          user?.photoURL || user?.picture || userProfile?.photoURL,
        authorIsModerator: true,
        title: "",
        body: trimmedBody,
        images,
      });
      qc.invalidateQueries(["hubPosts", hubId]);
      setBody("");
      setFiles([]);
      toast.success("Post published");
    } catch {
      toast.error("Failed to create post");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden p-6">
      <div className="flex items-start gap-3">
        <FeedAvatar
          initials={getInitials(authorName || "Admin")}
          name={authorName || "Admin"}
          avatarUrl={user?.photoURL || user?.picture || userProfile?.photoURL}
        />
        <textarea
          ref={textareaRef}
          value={body}
          onChange={(e) => {
            setBody(e.target.value);
            autoResize();
          }}
          placeholder="Type your post here"
          rows={2}
          className="flex-1 resize-none border-0 focus:ring-0 text-sm text-[#4B5563] placeholder-gray-400 leading-relaxed min-h-[72px]"
        />
      </div>
      {files.length > 0 && (
        <p className="text-xs text-gray-500 mt-2 ml-[56px]">
          {files.length} photo(s) selected
        </p>
      )}
      <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-100">
        <div className="w-11 shrink-0" aria-hidden />
        <div className="flex-1 flex items-center justify-between min-w-0">
          <label className="flex items-center gap-2 text-sm font-medium text-[#15415E] cursor-pointer hover:opacity-80">
            <Camera size={18} />
            Photo
            <input
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                const picked = Array.from(e.target.files || []).slice(0, 5);
                setFiles(picked);
              }}
            />
          </label>
          <button
            type="button"
            disabled={uploading || (!body.trim() && !files.length)}
            onClick={handleSubmit}
            className="px-5 py-2 text-sm font-semibold text-white bg-instant-teams-teal-Main rounded-lg disabled:opacity-40 shrink-0"
          >
            {uploading ? "Posting…" : "Post"}
          </button>
        </div>
      </div>
    </div>
  );
}
